import React, { type ComponentProps } from 'react';
import {
  defineMessages,
  FormattedMessage,
  useIntl,
  type MessageValue,
  type NoMessageValues,
} from 'react-intl';

import { type AddressType } from '@garnish/location';

import { BagHeaderLink } from '../BagHeaderLink';

/**
 * Location selector for the bag header.
 */
export const BagLocationHeader = (props: BagLocationHeaderProps) => {
  const {
    orderChannel,
    locationName,
    accessibilityRole,
    isDisabled,
    nonFocusable,
    requestDeliveryPreferencesChange,
    requestChannelOrLocationChange,
  } = props;

  const { formatMessage } = useIntl();

  const accessibilityLabel = formatMessage(messages.accessibilityLabel, {
    location: locationName,
  });

  const onPress =
    orderChannel === 'delivery' ? requestDeliveryPreferencesChange : requestChannelOrLocationChange;

  return (
    <BagHeaderLink
      accessibilityLabel={accessibilityLabel}
      accessibilityRole={accessibilityRole}
      isDisabled={isDisabled}
      nonFocusable={nonFocusable}
      onPress={onPress}
    >
      {locationName}
    </BagHeaderLink>
  );
};

export const BagLocationFromConnector = () => {
  return <FormattedMessage {...messages.from} />;
};

export const BagLocationToConnector = () => {
  return <FormattedMessage {...messages.to} />;
};

export const BagLocationAtConnector = () => {
  return <FormattedMessage {...messages.at} />;
};

// ─── Messages ───────────────────────────────────────────────────────────────

const messages = defineMessages<{
  readonly accessibilityLabel: { readonly location: MessageValue };
  readonly from: NoMessageValues;
  readonly to: NoMessageValues;
  readonly at: NoMessageValues;
}>({
  accessibilityLabel: {
    defaultMessage: 'Change location: {location}',
    description: 'Bag | Header | Change location',
  },
  from: {
    defaultMessage: 'from',
    description: 'Bag | Header | From',
  },
  to: {
    defaultMessage: 'to',
    description: 'Bag | Header | To',
  },
  at: {
    defaultMessage: 'at',
    description: 'Bag | Header | At',
  },
});

// ─── Types ──────────────────────────────────────────────────────────────────

type BagLocationHeaderProps = {
  locationName: string;
  orderChannel: AddressType;
  isDisabled?: boolean;
  nonFocusable?: boolean;
  accessibilityRole?: ComponentProps<typeof BagHeaderLink>['accessibilityRole'];
  requestDeliveryPreferencesChange: () => void;
  requestChannelOrLocationChange: () => void;
};
